import { create } from "zustand";
import type { StepGroupsData } from "@certa/queries/types/workflow.types";
import type { StepDetails } from "@certa/types";
import type { ComponentsToHighlight } from "../types/workflowpreview.types";
import { devLogger } from "@certa/common/utils/devLogger";
import {
  createStateSetter,
  createUniqueArraySetter
} from "@certa/common/utils/stateSetters";
import { getPreviewIdFromTag } from "../utils/transformMetaJSONToPlatformAPIData";
import type { StudioNodeType } from "@certa/studio/api";
import { studioDesignerURLRoutes } from "@certa/studio-common/src/utils";

type InContextComponent = {
  tag: string;
  type: StudioNodeType;
};

// ECA Rule component elements for scrolling and highlighting
type ECARuleElements = {
  events: Map<number, HTMLElement>;
  conditions: Map<number, HTMLElement>;
  actions: Map<string, HTMLElement>; // key: "ruleIndex-actionIndex"
};

type WorkflowPreviewState = {
  setSelectedStep: (stepId: number | null, swimlaneId: number | null) => void;
  setSelectedStepByTag: (tag: string) => void;

  fieldIdMap: Record<number, string>;
  stepIdMap: Record<number, string>;
  swimlaneIdMap: Record<number, string>;
  setComponentIdMap: (maps: {
    fieldIdMap: Record<number, string>;
    stepIdMap: Record<number, string>;
    swimlaneIdMap: Record<number, string>;
  }) => void;

  swimlanesData: StepGroupsData;
  setSwimlanesData: (data: StepGroupsData) => void;

  stepsDetails: Record<string, StepDetails>;
  setStepsDetails: (details: Record<string, StepDetails>) => void;

  componentsToHighlight: ComponentsToHighlight;
  setComponentsToHighlight: (components: ComponentsToHighlight) => void;

  expandedSwimlanes: number[];
  setExpandedSwimlanes: (
    swimlaneIds: number[] | ((current: number[]) => number[])
  ) => void;
  toggleExpandedSwimlane: (swimlaneId: number) => void;

  isPreviewEditModeEnabled: boolean;
  setIsPreviewEditModeEnabled: (
    enabled: boolean | ((prev: boolean) => boolean)
  ) => void;

  isInContextModeEnabled: boolean;
  setIsInContextModeEnabled: (
    enabled: boolean | ((prev: boolean) => boolean)
  ) => void;
  inContextComponents: InContextComponent[];
  setInContextComponents: (
    val:
      | InContextComponent[]
      | ((prev: InContextComponent[]) => InContextComponent[])
  ) => void;

  openAddRecordModal: boolean;
  setOpenAddRecordModal: (open: boolean | ((prev: boolean) => boolean)) => void;

  // ECA Rule elements management
  ecaRuleElements: ECARuleElements;
  setECARuleElements: (elements: ECARuleElements) => void;
  addECARuleElement: (
    type: "events" | "conditions" | "actions",
    key: number | string,
    element: HTMLElement
  ) => void;
  removeECARuleElement: (
    type: "events" | "conditions" | "actions",
    key: number | string
  ) => void;
  scrollToECARuleElement: (
    type: "events" | "conditions" | "actions",
    key: number | string
  ) => void;

  // Add global resize state
  isAnyFieldResizing: boolean;
  setIsAnyFieldResizing: (isResizing: boolean) => void;
};

const emptySwimlaneData: StepGroupsData = {
  taggedSteps: [],
  defaultStepTags: [],
  defaultStepTagsForGroup: [],
  todoStepGroups: [],
  todoSteps: [],
  progress: 0,
  results: [],
  myStepGroups: [],
  myTaggedSteps: [],
  userStepGroups: []
};

export const useProcessDetailsStore = create<WorkflowPreviewState>(
  (set, get) => ({
    swimlanesData: emptySwimlaneData,
    setSwimlanesData: data => set({ swimlanesData: data }),

    stepsDetails: {},
    setStepsDetails: details => set({ stepsDetails: details }),

    fieldIdMap: {},
    stepIdMap: {},
    swimlaneIdMap: {},
    setComponentIdMap: maps =>
      set({
        fieldIdMap: maps.fieldIdMap,
        stepIdMap: maps.stepIdMap,
        swimlaneIdMap: maps.swimlaneIdMap
      }),

    setSelectedStep: (stepId, swimlaneId) => {
      if (stepId && swimlaneId) {
        studioDesignerURLRoutes.onPreviewModeStepClick(stepId, swimlaneId);
      }
    }, // Use the query params
    setSelectedStepByTag: stepTag => {
      const selectedStep = getPreviewIdFromTag(stepTag);
      const selectedSwimlane = get().swimlanesData.results.find(lane =>
        lane.steps.some(step => step.id === selectedStep)
      );

      if (!selectedSwimlane) {
        devLogger.error(
          `Selected swimlane not found for step tag: ${stepTag}, step id: ${selectedStep}`
        );
        return;
      }

      if (selectedStep && selectedSwimlane) {
        studioDesignerURLRoutes.onPreviewModeStepClick(
          selectedStep,
          selectedSwimlane.id
        );
      }
    },

    componentsToHighlight: {
      updatedComponentIds: [],
      addedComponentIds: [],
      deletedComponentIds: [],
      hoveredComponentId: null
    },

    setComponentsToHighlight: components =>
      set({ componentsToHighlight: components }),

    expandedSwimlanes: [],
    setExpandedSwimlanes: createUniqueArraySetter<number, WorkflowPreviewState>(
      {
        set,
        key: "expandedSwimlanes"
      }
    ),
    toggleExpandedSwimlane: swimlaneId =>
      set(state => ({
        expandedSwimlanes: state.expandedSwimlanes.includes(swimlaneId)
          ? state.expandedSwimlanes.filter(id => id !== swimlaneId)
          : [...state.expandedSwimlanes, swimlaneId]
      })),

    isPreviewEditModeEnabled: true,
    setIsPreviewEditModeEnabled: createStateSetter<
      boolean,
      WorkflowPreviewState
    >({ set, key: "isPreviewEditModeEnabled" }),

    isInContextModeEnabled: false,
    setIsInContextModeEnabled: createStateSetter<boolean, WorkflowPreviewState>(
      { set, key: "isInContextModeEnabled" }
    ),

    inContextComponents: [],
    setInContextComponents: createStateSetter<
      InContextComponent[],
      WorkflowPreviewState
    >({ set, key: "inContextComponents" }),

    openAddRecordModal: false,
    setOpenAddRecordModal: createStateSetter<boolean, WorkflowPreviewState>({
      set,
      key: "openAddRecordModal"
    }),

    // ECA Rule elements management
    ecaRuleElements: {
      events: new Map(),
      conditions: new Map(),
      actions: new Map()
    },
    setECARuleElements: elements => set({ ecaRuleElements: elements }),
    addECARuleElement: (type, key, element) =>
      set(state => {
        if (type === "events" || type === "conditions") {
          const currentMap = state.ecaRuleElements[type];
          const newMap = new Map(currentMap);
          newMap.set(key as number, element);
          return {
            ecaRuleElements: {
              ...state.ecaRuleElements,
              [type]: newMap
            }
          };
        } else {
          const currentMap = state.ecaRuleElements[type];
          const newMap = new Map(currentMap);
          newMap.set(key as string, element);
          return {
            ecaRuleElements: {
              ...state.ecaRuleElements,
              [type]: newMap
            }
          };
        }
      }),
    removeECARuleElement: (type, key) => {
      set(state => {
        if (type === "events" || type === "conditions") {
          const currentMap = state.ecaRuleElements[type];
          const newMap = new Map(currentMap);
          newMap.delete(key as number);
          return {
            ecaRuleElements: {
              ...state.ecaRuleElements,
              [type]: newMap
            }
          };
        } else {
          const currentMap = state.ecaRuleElements[type];
          const newMap = new Map(currentMap);
          newMap.delete(key as string);
          return {
            ecaRuleElements: {
              ...state.ecaRuleElements,
              [type]: newMap
            }
          };
        }
      });
    },
    scrollToECARuleElement: (type, key) => {
      if (type === "events" || type === "conditions") {
        const element = get().ecaRuleElements[type].get(key as number);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        const element = get().ecaRuleElements[type].get(key as string);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    },

    isAnyFieldResizing: false,
    setIsAnyFieldResizing: (isResizing: boolean) =>
      set({ isAnyFieldResizing: isResizing })
  })
);
